import { useEffect, useRef, type ReactNode } from 'react';

// THE reusable disclosure region — the app's one way to open and close a strip
// of content in place.
//
// Lifted from the rota's Hours & wages strip (`.wm-reveal` / `.wm-clip`), whose
// own comments explain why it is built this way and which are worth keeping
// together with the code:
//
//   - `grid-template-rows: 0fr → 1fr` animates to the content's OWN height, so
//     there is no magic max-height to guess and overshoot. A two-row body opens
//     as smoothly as a twenty-row one.
//   - The body stays MOUNTED whether open or not. "A conditionally rendered
//     child cannot be animated, it can only appear" — and, just as important,
//     it cannot be animated AWAY either: unmount it on close and the region
//     collapses an empty box while the content vanishes on the first frame.
//     That is the difference between a close that slides and one that snaps.
//   - The clip needs `min-height: 0` or it refuses to collapse below its
//     content.
//
// ONE REGION PER DISCLOSURE. Several triggers must not share a region: the
// shared one has to swap its body as the open item changes, which is the
// unmount-on-close problem wearing a different hat.
//
// Closed content is hidden from assistive tech AND made inert — a clipped
// button is still tabbable otherwise, so a keyboard user falls into a panel
// they cannot see. Set as a DOM property because React 18's types predate
// `inert`.

export interface DisclosureRegionProps {
  open: boolean;
  children: ReactNode;
  /** Extra class on the region, for hosts that need to position it. */
  className?: string;
}

export function DisclosureRegion({ open, children, className }: DisclosureRegionProps) {
  const clipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const clip = clipRef.current as (HTMLDivElement & { inert?: boolean }) | null;
    if (clip) clip.inert = !open;
  }, [open]);

  return (
    <div className={'disclosure' + (open ? ' open' : '') + (className ? ' ' + className : '')} aria-hidden={!open}>
      <div className="disclosure-clip" ref={clipRef}>
        {children}
      </div>
    </div>
  );
}
